/**
 * COMPANY HOME — level 0 of the grammar (09 §1.4). `/c/{company_uid}`.
 *
 * It lists the modules for this company and nothing else. Every tile is a NOUN, because 09 §1.3 is
 * binding: a noun is a screen, a verb is an action. There is no company dropdown on this screen —
 * the company was chosen in the context bar and nowhere else (§3.4).
 *
 * A module the tenant did not buy, or that has not been built, renders as a written Hebrew state
 * with a next step — never as a fake screen, and never as a hidden tile. 09 §1.3 last row: "a
 * toolbar button that only some users may press" is a screen the gate governs, with a written
 * reason when denied.
 */

import { useEffect } from 'react';
import { AppShell } from '../shell/AppShell';
import { useDeclareScreenContext, useWorkContext } from '../shell/ContextProvider';
import { StateBlock } from '../components/StateBlock';
import { routes } from '../routes';
import { denialTextFor } from '../api/denialText';

export interface CompanyHomeScreenProps {
  readonly companyUid: string;
}

export function CompanyHomeScreen({ companyUid }: CompanyHomeScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const { setCompanyByUid, setModuleByCode, setEntityLabel } = useDeclareScreenContext();

  // The screen DECLARES what it is showing so the bar can name it. It does not choose it.
  useEffect(() => {
    setCompanyByUid(companyUid);
    setModuleByCode(null);
    setEntityLabel(null);
  }, [companyUid, setCompanyByUid, setModuleByCode, setEntityLabel]);

  if (ctx.company === null) {
    return (
      <AppShell titleHe="דף הבית של החברה">
        <StateBlock
          titleHe="החברה עדיין נטענת"
          bodyHe="אם המצב הזה נשאר, ייתכן שהקישור מפנה לחברה שאינה זמינה לך."
          nextStep={<a className="btn" href={routes.companies}>חזרה לחברות שלי</a>}
        />
      </AppShell>
    );
  }

  const company = ctx.company;

  return (
    <AppShell titleHe={company.legalName}>
      <section className="tiles">
        {ctx.modules.map((m) => {
          const reachable = m.available && m.catalogueStatus === 'active';
          return (
            <article key={m.moduleCode} className="tile" data-available={reachable ? 'true' : 'false'}>
              <h2 className="tile__title">{m.displayNameHe}</h2>
              {reachable ? (
                <a className="btn btn--primary" href={routes.moduleHome(company.companyUid, m.moduleCode)}>
                  פתיחה
                </a>
              ) : (
                <>
                  {/*
                    Only `company_management` and `billing` are `active` in the catalogue. Everything
                    else is `planned`: the code is registered so a SKU can map to it, but NO MODULE
                    CODE EXISTS. Rendering a business screen for it would be exactly the "written,
                    correct, documented as working, and reaching nothing" pattern G-03 exists to catch.
                  */}
                  <p className="tile__unavailable">
                    {m.catalogueStatus === 'planned'
                      ? 'לא זמין — החלק הזה טרם נפתח במערכת'
                      : denialTextFor(m.unavailableReason).titleHe}
                  </p>
                  <p className="tile__next-step">{denialTextFor(m.unavailableReason).nextStepHe}</p>
                </>
              )}
            </article>
          );
        })}
      </section>

      {/* 09 §5.9 — provenance is stated, never assumed. */}
      <p className="provenance">מצב החברה מוצג כפי שנרשם במשרד ואינו נגזר מהיעדר סימון.</p>
    </AppShell>
  );
}
